// Tipos compartidos entre el frontend (src/) y la API (functions/).
// Reflejan 1:1 el esquema de migrations/0001_initial.sql.

export type Rol =
  | "ADMINISTRADOR"
  | "ABOGADO"
  | "PROCURADOR"
  | "ADMINISTRATIVO"
  | "RECEPCION";

// Catálogo cerrado de permisos granulares. Los roles son, en el backend,
// simples conjuntos por defecto de este catálogo (ver lib/permisos.ts) — no
// hay ningún `if (rol === 'X')` disperso por las rutas.
export type Permiso =
  | "usuarios.ver"
  | "usuarios.crear"
  | "usuarios.editar"
  | "usuarios.desactivar"
  | "usuarios.reactivar"
  | "usuarios.restablecer_password"
  | "usuarios.cambiar_rol"
  | "usuarios.ver_actividad"
  | "roles.ver"
  | "roles.administrar"
  | "permisos.ver"
  | "permisos.administrar"
  | "clientes.ver"
  | "clientes.crear"
  | "clientes.editar"
  | "clientes.desactivar"
  | "expedientes.ver_todos"
  | "expedientes.ver_asignados"
  | "expedientes.crear"
  | "expedientes.editar"
  | "expedientes.archivar"
  | "expedientes.reasignar"
  | "expedientes.ver_evaluacion"
  | "expedientes.editar_evaluacion"
  | "catalogos_estudio.ver"
  | "catalogos_estudio.administrar"
  | "actuaciones.ver"
  | "actuaciones.crear"
  | "actuaciones.editar"
  | "actuaciones.eliminar"
  | "documentos.ver"
  | "documentos.subir"
  | "documentos.descargar"
  | "documentos.eliminar"
  | "agenda.ver"
  | "agenda.crear"
  | "agenda.editar"
  | "agenda.eliminar"
  | "economia.ver"
  | "economia.crear"
  | "economia.editar"
  | "economia.anular"
  | "economia.administrar_catalogos"
  | "economia.autorizar_descuento_extraordinario"
  | "economia.configurar_jus"
  | "auditoria.ver"
  | "configuracion.ver"
  | "configuracion.editar"
  | "procesos.ver"
  | "procesos.administrar"
  | "procesos.mover_etapa";

export type TipoCliente = "Persona Física" | "Persona Jurídica";

export type EstadoExpediente =
  | "Consulta Inicial"
  | "En Trámite"
  | "Con Resolución"
  | "En Ejecución o Cobro"
  | "Archivado"
  | "Desistido";

export type Prioridad = "Alta" | "Media" | "Baja";

// Evaluación y estrategia del expediente. Escalas cerradas y universales
// (no administrables por tenant, a diferencia de Estado procesal/Valor
// estratégico) — ver migrations/0008_evaluacion_estrategia.sql.
export type NivelRiesgo = "Bajo" | "Medio" | "Alto" | "Crítico";

export type ProbabilidadExito =
  | "No evaluada"
  | "Muy baja"
  | "Baja"
  | "Media"
  | "Alta"
  | "Muy alta";

export type EstadoEvento = "Pendiente" | "Cumplido" | "Cancelado" | "Vencido";

export type Moneda = "ARS" | "USD";

// GET /economia siempre devuelve los saldos agrupados por moneda — ARS y
// USD nunca se combinan en un único número (bug de integridad corregido:
// antes se sumaban sin distinguir moneda). Ver calcularSaldos en
// functions/_app/lib/economia.ts.
export interface SaldoPorMoneda {
  moneda: Moneda;
  saldoCliente: number;
  saldoEstudio: number;
}

export type TipoMovimientoEconomico =
  | "Capital del Cliente"
  | "Honorarios Pactados"
  | "Honorarios Regulados"
  | "Honorarios Cobrados"
  | "Gasto Adelantado"
  | "Gasto Recuperado"
  | "Pago Recibido"
  | "Pago al Cliente"
  | "Pago a Profesional"
  | "Aporte"
  | "Retención"
  | "Otro Ingreso"
  | "Otro Egreso";

// Naturaleza de Categoría/Subcategoría económica — independiente de
// TipoMovimientoEconomico (ver migrations/0010_categorias_economicas.sql,
// análisis crítico: el `tipo` ya es un catálogo cerrado con semántica
// contable propia; esta es una clasificación de gestión adicional,
// administrable por tenant, que solo aplica a los tipos de movimiento que
// representan un ingreso o un gasto real del estudio).
export type NaturalezaCategoriaEconomica = "Ingreso" | "Gasto";

// Categoría/Subcategoría económica: catálogo propio de CADA ESTUDIO
// (tenant_id, permiso economia.administrar_catalogos) — mismo patrón que
// Estado procesal/Valor estratégico.
export interface CategoriaEconomica {
  id: string;
  tenant_id: string;
  naturaleza: NaturalezaCategoriaEconomica;
  nombre: string;
  descripcion: string | null;
  orden: number;
  activo: boolean;
  creado_en: string;
  actualizado_en: string | null;
}

export interface SubcategoriaEconomica {
  id: string;
  tenant_id: string;
  categoria_id: string;
  nombre: string;
  descripcion: string | null;
  orden: number;
  activo: boolean;
  creado_en: string;
  actualizado_en: string | null;
}

export type ModalidadHonorario =
  | "Porcentaje"
  | "Monto Fijo"
  | "Mixto"
  | "Regulado Judicialmente"
  | "Extrajudicial";

// "Estimado" cumple el rol de borrador/simulación inicial. Enviado/
// Rechazado/Vencido/Cancelado son las etapas comerciales previas al pacto
// (agregadas para el módulo integral de Honorarios); Pactado/Regulado/
// Cobrado/Anulado son las mismas etapas ya existentes desde el bloque
// original de Economía. Ver functions/_app/lib/honorarios/estados.ts para
// la matriz de transiciones válidas.
export type EstadoHonorario =
  | "Estimado"
  | "Enviado"
  | "Rechazado"
  | "Vencido"
  | "Cancelado"
  | "Pactado"
  | "Regulado"
  | "Cobrado"
  | "Anulado";

// Máquina de estados del ciclo de vida de un Honorario — única fuente de
// verdad, importada tanto por functions/_app/lib/honorarios/estados.ts
// (que la usa para VALIDAR, con autoridad real) como por el frontend (que
// la usa sólo para no OFRECER en el <select> una transición que el
// backend va a rechazar de todos modos — nunca reemplaza la validación
// real). Antes de este bloque, esta máquina estaba duplicada a mano en el
// backend y el frontend mostraba las 9 opciones siempre, sin filtrar.
export const TRANSICIONES_HONORARIO: Record<EstadoHonorario, EstadoHonorario[]> = {
  Estimado: ["Enviado", "Pactado", "Anulado"],
  Enviado: ["Rechazado", "Vencido", "Cancelado", "Pactado"],
  Rechazado: ["Estimado"],
  Vencido: ["Estimado"],
  Cancelado: [],
  Pactado: ["Regulado", "Cobrado", "Anulado"],
  Regulado: ["Cobrado", "Anulado"],
  Cobrado: ["Anulado"],
  Anulado: [],
};

export type TipoDescuentoHonorario = "Porcentaje" | "Importe";

export type FrecuenciaCuotas = "semanal" | "quincenal" | "mensual";

export type EstadoCuotaHonorario = "Pendiente" | "Pagada" | "Cancelada" | "Vencida";

// Ver migrations/0015_modulo_honorarios.sql. "Vencida" nunca se guarda —
// se calcula en el backend igual que Agenda (lib/agenda.ts), sólo viaja
// por la red en la respuesta ya resuelta.
export interface CuotaHonorario {
  id: string;
  tenant_id: string;
  honorario_id: string;
  numero: number;
  vencimiento: string;
  capital: number;
  interes: number;
  importe_total: number;
  estado: EstadoCuotaHonorario;
  movimiento_id: string | null;
  pagado_en: string | null;
  creado_en: string;
}

export type EstadoRendicion = "Cerrada" | "Anulada";

export type TipoAuditoriaAutenticacion =
  "LOGIN_EXITOSO" | "LOGIN_FALLIDO" | "LOGOUT";

export type EstadoTenant = "Activo" | "Suspendido";

export interface Tenant {
  id: string;
  nombre: string;
  slug: string;
  direccion: string | null;
  telefono: string | null;
  email_contacto: string | null;
  nombre_comercial: string | null;
  cuit: string | null;
  estado: EstadoTenant;
  plan: string;
  zona_horaria: string;
  moneda_principal: Moneda;
  observaciones_internas: string | null;
  // Ver migrations/0009_dashboard_ejecutivo.sql — clave fija en R2
  // (bucket DOCUMENTS), NULL = sin logo, se usa el ícono por defecto.
  logo_r2_key: string | null;
  activo: boolean;
  creado_en: string;
}

// Objetivos mensuales del panel ejecutivo: una fila por tenant (no
// historizada por mes — ver migrations/0009_dashboard_ejecutivo.sql).
// Cualquier objetivo en null significa "sin definir" — el Dashboard debe
// mostrar "Sin objetivo definido", nunca una barra de progreso al 0%.
export interface ObjetivosMensuales {
  id: string;
  tenant_id: string;
  honorarios_cobrados_objetivo: number | null;
  moneda_objetivo: Moneda;
  escritos_objetivo: number | null;
  audiencias_objetivo: number | null;
  nuevos_clientes_objetivo: number | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

export interface Usuario {
  id: string;
  tenant_id: string;
  email: string;
  nombre_completo: string;
  nombre: string | null;
  apellido: string | null;
  telefono: string | null;
  matricula: string | null;
  cargo: string | null;
  foto_url: string | null;
  rol: Rol;
  debe_cambiar_password: boolean;
  password_actualizado_en: string | null;
  activo: boolean;
  ultimo_acceso_en: string | null;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

export interface UsuarioPermisoOverride {
  id: string;
  tenant_id: string;
  usuario_id: string;
  permiso: Permiso;
  concedido: boolean;
  creado_en: string;
  creado_por: string | null;
}

export interface Cliente {
  id: string;
  tenant_id: string;
  tipo_cliente: TipoCliente;
  nombre_completo: string;
  identificacion: string | null;
  email: string | null;
  telefono: string | null;
  domicilio: string | null;
  localidad: string | null;
  observaciones: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

// Vocabulario controlado de color semántico — nunca CSS/hex arbitrario. El
// mismo identificador se resuelve siempre a las mismas clases/hex vía
// resolverColorSemantico() (src/lib/colorSemantico.ts), para que un concepto
// (ej. Materia "Laboral") se vea igual en toda la app.
export const COLORES_SEMANTICOS = [
  "red",
  "green",
  "blue",
  "violet",
  "orange",
  "amber",
  "cyan",
  "slate",
] as const;
export type ColorSemantico = (typeof COLORES_SEMANTICOS)[number];

// Materia/Submateria/Fuero: catálogos de plataforma (sin tenant_id),
// administrados por el SUPERADMIN vía /api/superadmin/catalogos. Reemplazan
// al enum plano `tipo_expediente` — ver migrations/0006_taxonomia_juridica.sql.
export interface Materia {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

export interface Submateria {
  id: string;
  materia_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

export interface Fuero {
  id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Organismo/Secretaria: ubicación procesal del expediente (a diferencia de
// Materia/Submateria/Fuero, son opcionales — ver migrations/
// 0007_organismos_secretarias.sql). Organismo pertenece a un Fuero;
// Secretaria pertenece a un Organismo (no todos los organismos tienen).
export interface Organismo {
  id: string;
  fuero_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

export interface Secretaria {
  id: string;
  organismo_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Estado procesal/Valor estratégico: catálogos propios de CADA ESTUDIO
// (llevan tenant_id y se administran desde el propio tenant, permiso
// catalogos_estudio.administrar) — a diferencia de Materia/Fuero/Organismo,
// que son de plataforma. Ver migrations/0008_evaluacion_estrategia.sql.
export interface EstadoProcesal {
  id: string;
  tenant_id: string;
  nombre: string;
  color: string | null;
  orden: number;
  activo: boolean;
  creado_en: string;
}

export interface ValorEstrategico {
  id: string;
  tenant_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Catálogo de Tipos de Actuación — mismo shape que ValorEstrategico, usado
// para que Actuacion.tipo sea un string elegido de una lista controlada
// por el estudio en vez de texto libre (ver routes/catalogos-estudio.ts).
export interface TipoActuacion {
  id: string;
  tenant_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Catálogo de Tipo de Honorario — mismo shape que TipoActuacion.
// honorarios.tipo_honorario sigue siendo TEXT (sin FK): el catálogo solo
// controla qué strings son elegibles desde el combo (ver catalogos-estudio.ts).
export interface TipoHonorario {
  id: string;
  tenant_id: string;
  nombre: string;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Área de Práctica: dimensión de visibilidad interna del estudio, ortogonal
// a Materia/Submateria/Fuero (taxonomía jurídica) y a roles/permisos (qué
// puede hacer un usuario, no qué ve). Catálogo propio de cada estudio,
// mismo patrón que Estado procesal/Valor estratégico — ver
// migrations/0012_areas_practica.sql. De cara al usuario esta pantalla se
// llama "Unidad de Negocio" (ver migrations/0019_unidades_negocio.sql) —
// el identificador interno nunca se renombró para no romper los ~15
// puntos de filtrado por área ya construidos.
export interface AreaPractica {
  id: string;
  tenant_id: string;
  nombre: string;
  color: string | null;
  responsable_usuario_id: string | null;
  descripcion: string | null;
  orden: number;
  activo: boolean;
  creado_en: string;
}

// Distribución de gastos generales entre Unidades de Negocio (ver
// migrations/0019_unidades_negocio.sql). `metodo` queda abierto a futuros
// valores (facturacion/cantidad_expedientes/horas/honorarios_cobrados)
// pero solo estos dos tienen lógica implementada hoy.
export type MetodoDistribucionGastos = "porcentaje_fijo" | "partes_iguales";

export interface DistribucionGastosGenerales {
  id: string;
  tenant_id: string;
  metodo: MetodoDistribucionGastos;
  actualizado_en: string;
  actualizado_por: string | null;
}

export interface DistribucionGastosUnidadPorcentaje {
  id: string;
  tenant_id: string;
  area_practica_id: string;
  porcentaje: number;
}

// Valor JUS: índice provincial (Ley 9.131 de Mendoza) usado por el motor de
// honorarios, configurado por CADA ESTUDIO (tenant_id) — dos estudios
// pueden tener valores vigentes distintos. Ver migrations/0016_valores_jus_tenant.sql.
// `valor` y `vigencia_desde` son inmutables tras la creación (ver
// routes/valores-jus.ts) — solo fuente/observacion/vigencia_hasta se editan.
export interface ValorJus {
  id: string;
  tenant_id: string;
  valor: number;
  vigencia_desde: string;
  vigencia_hasta: string | null;
  fuente: string;
  observacion: string | null;
  creado_por: string;
  creado_en: string;
  actualizado_en: string;
}

export interface EvaluacionHistorialItem {
  id: string;
  tenant_id: string;
  expediente_id: string;
  campo: string;
  valor_anterior: string | null;
  valor_nuevo: string | null;
  usuario_id: string;
  usuario_nombre?: string;
  creado_en: string;
}

export interface AuditoriaEventoItem {
  id: string;
  origen: "ESTUDIO" | "PLATAFORMA" | "SISTEMA";
  actor_id: string;
  actor_nombre?: string | null;
  accion: string;
  entidad_tipo: string;
  entidad_id: string | null;
  detalle: string | null;
  creado_en: string;
}

export interface Expediente {
  id: string;
  tenant_id: string;
  numero_expediente: string;
  cliente_id: string;
  materia_id: string;
  submateria_id: string;
  fuero_id: string;
  organismo_id: string | null;
  secretaria_id: string | null;
  // Área responsable: obligatoria en el formulario solo si el tenant ya
  // tiene áreas activas definidas — ver lib/areas-practica.ts.
  area_practica_id: string | null;
  // Denormalizados: solo vienen poblados cuando la ruta hace el JOIN a los
  // catálogos (mismo patrón que `cliente_nombre` en el frontend) — no son
  // columnas propias de la tabla.
  materia_nombre?: string;
  materia_color?: string | null;
  submateria_nombre?: string;
  fuero_nombre?: string;
  fuero_color?: string | null;
  organismo_nombre?: string;
  secretaria_nombre?: string;
  area_practica_nombre?: string;
  area_practica_color?: string | null;
  // Motor de Procesos de Trabajo: siempre opcional, nunca obligatorio ("el
  // sistema nunca impone un flujo") — ver migrations/0020_procesos_trabajo.sql.
  // etapa_responsable_id es el responsable de la etapa actual del flujo de
  // trabajo, un concepto distinto de abogado_responsable_id (responsable
  // del expediente en general, no se toca).
  proceso_id?: string | null;
  etapa_actual_id?: string | null;
  etapa_responsable_id?: string | null;
  etapa_desde?: string | null;
  proceso_desde?: string | null;
  proceso_nombre?: string;
  proceso_color?: string | null;
  etapa_nombre?: string;
  etapa_color?: string | null;
  etapa_responsable_nombre?: string;
  // Evaluación y estrategia: presentes solo si el usuario tiene el permiso
  // expedientes.ver_evaluacion — el backend los elimina de la respuesta
  // cuando falta (ver routes/expedientes.ts), por eso son opcionales acá.
  estado_procesal_id?: string | null;
  estado_procesal_nombre?: string;
  estado_procesal_color?: string | null;
  nivel_riesgo?: NivelRiesgo | null;
  fundamento_riesgo?: string | null;
  probabilidad_exito?: ProbabilidadExito;
  fundamento_probabilidad?: string | null;
  valoresEstrategicos?: ValorEstrategico[];
  rol_cliente: string | null;
  estado: EstadoExpediente;
  abogado_responsable_id: string | null;
  // Nombre resuelto de abogado_responsable_id — ver joinsCatalogosExpediente()
  // en functions/_app/lib/catalogos.ts (mismo patrón de LEFT JOIN a usuarios
  // que ya se usa para etapa_responsable_nombre). Opcional porque sólo viene
  // resuelto en las rutas que usan ese helper compartido.
  abogado_responsable_nombre?: string | null;
  tipo_reclamo: string | null;
  fecha_apertura: string;
  fecha_cierre: string | null;
  // Cronómetro del expediente: "Inicio del juicio" no es derivable de
  // ningún otro campo (a diferencia de "Ingreso" = fecha_apertura), por
  // eso es un campo manual y opcional.
  fecha_inicio_juicio: string | null;
  // Barra de progreso: avance configurado a mano por el abogado — no
  // representa un porcentaje procesal real.
  avance_manual_pct: number;
  prioridad: Prioridad;
  monto_reclamado_estimado: number | null;
  observaciones: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

// Motor de Procesos de Trabajo — ver migrations/0020_procesos_trabajo.sql.
// Deliberadamente separado de estados_procesales/estado_procesal_id
// (evaluación jurídica genérica del estudio, con permisos propios): un
// Proceso agrupa Etapas ordenadas de un flujo de trabajo interno
// configurable, ortogonal tanto a Materia como a Unidad de Negocio.
export interface ProcesoTrabajo {
  id: string;
  tenant_id: string;
  nombre: string;
  descripcion: string | null;
  color: string | null;
  orden: number;
  activo: boolean;
  bloquear_avance_si_checklist_pendiente: boolean;
  creado_en: string;
  // Denormalizados: solo vienen poblados cuando la ruta trae las M:N.
  materiaIds?: string[];
  areaPracticaIds?: string[];
}

export interface EtapaProceso {
  id: string;
  tenant_id: string;
  proceso_id: string;
  nombre: string;
  color: string | null;
  orden: number;
  responsable_usuario_id: string | null;
  tiempo_objetivo_dias: number | null;
  descripcion: string | null;
  activo: boolean;
  creado_en: string;
  responsable_nombre?: string;
  // Denormalizados: solo vienen poblados en GET /procesos/:procesoId/etapas
  // (Diseñador Visual) — evitan un fetch por etapa para mostrar los badges.
  checklist_count?: number;
  documentos_count?: number;
  automatizaciones_count?: number;
}

export interface EtapaChecklistItem {
  id: string;
  tenant_id: string;
  etapa_id: string;
  texto: string;
  obligatorio: boolean;
  orden: number;
  // Agrupación libre para checklists largos (ej. "A. Hecho y
  // responsabilidad", "B. Lesiones") — ver migrations/0021_accidentes_transito.sql.
  categoria: string | null;
}

// Estados del checklist documental extendido (Requerimiento "Checklist
// documental" del bloque Accidentes de Tránsito) — aplican sólo cuando el
// Proceso los usa; el resto sigue con el `completado` binario de siempre.
export const ESTADOS_CHECKLIST_DOCUMENTAL = [
  "No requerido",
  "Pendiente",
  "Solicitado",
  "Recibido",
  "Validado",
  "Observado",
] as const;
export type EstadoChecklistDocumental = (typeof ESTADOS_CHECKLIST_DOCUMENTAL)[number];

export interface EtapaDocumentoSugerido {
  id: string;
  tenant_id: string;
  etapa_id: string;
  nombre: string;
  orden: number;
}

// Automatización al entrar a la Etapa: crea una Actuación (misma lógica
// atómica que POST /actuaciones) y, opcionalmente, su vencimiento de
// Agenda asociado a N días — cubre en un solo mecanismo "Tareas
// automáticas" + "Actuaciones automáticas" + "Agenda automática".
export interface EtapaAutomatizacion {
  id: string;
  tenant_id: string;
  etapa_id: string;
  tipo_actuacion: string;
  descripcion: string;
  genera_vencimiento: boolean;
  tipo_evento_vencimiento: string | null;
  dias_vencimiento: number | null;
  prioridad_vencimiento: string | null;
  orden: number;
}

export interface ExpedienteChecklistEstado {
  id: string;
  tenant_id: string;
  expediente_id: string;
  checklist_item_id: string;
  completado: boolean;
  completado_en: string | null;
  completado_por: string | null;
  // Checklist documental extendido (opcional, ver ESTADOS_CHECKLIST_DOCUMENTAL).
  estado: EstadoChecklistDocumental | null;
  responsable_id: string | null;
  responsable_nombre?: string;
  fecha_solicitud: string | null;
  fecha_recepcion: string | null;
  observacion: string | null;
  documento_id: string | null;
  documento_titulo?: string;
}

export interface ExpedienteEtapaHistorialItem {
  id: string;
  tenant_id: string;
  expediente_id: string;
  etapa_anterior_id: string | null;
  etapa_nueva_id: string;
  usuario_id: string;
  usuario_nombre?: string;
  responsable_nuevo_id: string | null;
  responsable_nuevo_nombre?: string;
  observaciones: string | null;
  creado_en: string;
  // Denormalizados para no requerir un JOIN aparte en el frontend.
  etapa_anterior_nombre?: string | null;
  etapa_nueva_nombre?: string;
}

// Diseñador Visual de Procesos: detalle completo de una plantilla
// preconfigurada (GET /procesos/plantillas/:clave) para la Vista previa
// antes de "Duplicar" — no confundir con ProcesoTrabajo/EtapaProceso (eso
// es un Proceso ya materializado del tenant; esto es sólo la plantilla de
// código, de solo lectura).
export interface PlantillaAutomatizacionDetalle {
  tipoActuacion: string;
  descripcion: string;
  generaVencimiento?: boolean;
  tipoEventoVencimiento?: string;
  diasVencimiento?: number;
  prioridadVencimiento?: "Alta" | "Media" | "Baja";
}

export interface PlantillaChecklistItemDetalle {
  texto: string;
  obligatorio: boolean;
  categoria?: string;
}

export interface PlantillaEtapaDetalle {
  nombre: string;
  color?: string;
  tiempoObjetivoDias?: number;
  descripcion?: string;
  checklist: PlantillaChecklistItemDetalle[];
  documentosSugeridos: string[];
  automatizaciones: PlantillaAutomatizacionDetalle[];
}

export interface PlantillaProcesoDetalle {
  clave: string;
  nombre: string;
  descripcion: string;
  areaJuridica: string | null;
  complejidad: "Simple" | "Media" | "Compleja" | null;
  usuariosRecomendados: string | null;
  cantidadEtapas: number;
  duracionEstimadaDias: number | null;
  etapas: PlantillaEtapaDetalle[];
}

// PATCH /procesos/:procesoId/etapas/orden — reordenamiento atómico desde
// el drag&drop del Diseñador Visual (nunca se usa PUT /etapas/:etapaId
// para esto: ese endpoint sobrescribe sin COALESCE el resto de los
// campos de la etapa).
export interface BodyReordenEtapas {
  ordenes: { id: string; orden: number }[];
}

export type TipoPersona = "Persona Humana" | "Persona Jurídica";

export interface Parte {
  id: string;
  tenant_id: string;
  expediente_id: string;
  rol: string;
  nombre_completo: string;
  tipo_persona: TipoPersona;
  documento: string | null;
  telefono: string | null;
  observaciones: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
}

export interface Actuacion {
  id: string;
  tenant_id: string;
  expediente_id: string;
  tipo: string;
  fecha: string;
  hora: string | null;
  descripcion: string;
  estado: string;
  resultado: string | null;
  prioridad: Prioridad;
  confidencial: boolean;
  responsable_id: string | null;
  genera_vencimiento: boolean;
  // Centro de Operaciones: marcado manual — no hay señal automática para
  // detectar que un cliente está esperando respuesta.
  requiere_respuesta_cliente: boolean;
  etiquetas: string | null;
  observaciones: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
  // Generalización reutilizada por audiencias OCC, notificaciones/cédulas,
  // pericias, oficios y testigos (ver migrations/0021_accidentes_transito.sql)
  // — evita crear una tabla nueva por cada uno de esos dominios.
  lugar_o_enlace: string | null;
  monto_ofrecido: number | null;
  monto_acordado: number | null;
  parte_id: string | null;
  parte_nombre?: string;
  // Denormalizado en GET /actuaciones (LEFT JOIN usuarios), mismo criterio
  // que parte_nombre — el nombre no depende de que quien consulta tenga
  // usuarios.ver.
  responsable_nombre?: string | null;
}

export interface EventoAgenda {
  id: string;
  tenant_id: string;
  expediente_id: string;
  actuacion_id: string | null;
  tipo_evento: string;
  titulo: string;
  descripcion: string | null;
  fecha_vencimiento: string;
  hora: string | null;
  responsable_id: string | null;
  prioridad: Prioridad;
  estado: EstadoEvento;
  observaciones: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
  // Denormalizado en GET /agenda (LEFT JOIN usuarios) para que el nombre del
  // responsable se muestre igual sin importar si el rol de quien consulta
  // tiene o no el permiso usuarios.ver.
  responsable_nombre?: string | null;
}

export interface Documento {
  id: string;
  tenant_id: string;
  titulo: string;
  descripcion: string | null;
  categoria: string | null;
  tipo_archivo: string | null;
  nombre_archivo: string;
  extension: string | null;
  peso_bytes: number | null;
  mime_type: string | null;
  r2_key: string;
  hash_archivo: string | null;
  cliente_id: string | null;
  expediente_id: string | null;
  actuacion_id: string | null;
  honorario_id: string | null;
  // Autorreferencia: apunta a la primera versión de la familia (null en la
  // raíz misma). "version" cuenta 1,2,3... dentro de esa familia.
  documento_raiz_id: string | null;
  modulo_origen: string | null;
  estado: string;
  confidencial: boolean;
  etiquetas: string | null;
  fecha_vencimiento: string | null;
  observaciones: string | null;
  version: number;
  // Reservados para OCR/IA — sin lógica todavía que los lea o escriba.
  texto_extraido: string | null;
  procesado_ia: boolean;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
}

export interface MovimientoEconomico {
  id: string;
  tenant_id: string;
  expediente_id: string | null;
  cliente_id: string | null;
  caja_id: string | null;
  honorario_id: string | null;
  rendicion_id: string | null;
  cuota_id: string | null;
  tipo: TipoMovimientoEconomico;
  // Clasificación de gestión, opcional — solo aplica a los tipos con
  // naturaleza Ingreso/Gasto real (ver lib/economia.ts, NATURALEZA_CATEGORIA).
  categoria_id: string | null;
  subcategoria_id: string | null;
  // Denormalizados: solo vienen poblados cuando la ruta hace el JOIN
  // (ver routes/economia.ts) — no son columnas propias de la tabla.
  categoria_nombre?: string | null;
  subcategoria_nombre?: string | null;
  // Unidad de Negocio heredada del expediente vinculado (si lo hay) — un
  // movimiento sin expediente_id es "Gasto General del Estudio", por eso
  // estos dos campos vienen null/ausentes en ese caso, nunca un valor
  // propio duplicado en la tabla.
  area_practica_nombre?: string | null;
  area_practica_color?: string | null;
  // Sólo se resuelve cuando honorario_id está presente (vía el honorario
  // vinculado → profesional_beneficiario_id) — no es una columna propia
  // de la tabla. Es la atribución de "Pago a Profesional" a un profesional
  // concreto, cuando el movimiento se generó ligado a un Honorario; si no
  // hay honorario_id, no hay forma de resolverlo sin cambiar el esquema
  // (ver informe de la fase de integridad — deuda documentada, no
  // inventada).
  profesional_beneficiario_id?: string | null;
  profesional_beneficiario_nombre?: string | null;
  concepto: string;
  fecha: string;
  importe: number;
  moneda: Moneda;
  estado: string;
  observaciones: string | null;
  creado_en: string;
  creado_por: string | null;
}

export interface Caja {
  id: string;
  tenant_id: string;
  nombre: string;
  moneda: Moneda;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
}

export interface Honorario {
  id: string;
  tenant_id: string;
  expediente_id: string;
  tipo_honorario: string | null;
  modalidad: ModalidadHonorario;
  porcentaje: number | null;
  importe_fijo: number | null;
  base_calculo: string | null;
  honorario_estimado: number | null;
  honorario_pactado: number | null;
  honorario_regulado: number | null;
  moneda: Moneda;
  profesional_beneficiario_id: string | null;
  fecha_pacto: string | null;
  fecha_regulacion: string | null;
  documento_id: string | null;
  estado: EstadoHonorario;
  observaciones: string | null;
  // Snapshot del cálculo legal (Ley 9.131) cuando la simulación usó el
  // motor de lib/honorarios/motor-honorarios.ts — null si se cargó a mano.
  articulo_aplicado: string | null;
  jus_aplicados: number | null;
  valor_jus_usado: number | null;
  porcentaje_aplicado: number | null;
  fuente_normativa: string | null;
  // Negociación comercial.
  subtotal_antes_descuento: number | null;
  descuento_tipo: TipoDescuentoHonorario | null;
  descuento_valor: number | null;
  motivo_descuento: string | null;
  descuento_autorizado_por: string | null;
  fecha_envio: string | null;
  validez_hasta: string | null;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

export interface Rendicion {
  id: string;
  tenant_id: string;
  expediente_id: string;
  cliente_id: string;
  total_recibido: number;
  capital_entregado: number;
  honorarios_descontados: number;
  gastos_descontados: number;
  retenciones: number;
  saldo: number;
  moneda: Moneda;
  fecha: string;
  metodo_pago: string | null;
  documento_id: string | null;
  observaciones: string | null;
  estado: EstadoRendicion;
  activo: boolean;
  creado_en: string;
  creado_por: string | null;
  actualizado_en: string | null;
  actualizado_por: string | null;
}

export interface Superadmin {
  id: string;
  email: string;
  nombre_completo: string;
  activo: boolean;
  ultimo_acceso_en: string | null;
  creado_en: string;
}

// Payload del JWT de sesión de SUPERADMIN — deliberadamente sin tenant_id ni rol
// de estudio: es un realm de autenticación separado del de usuarios de tenant.
export interface PayloadSesionSuperadmin {
  sub: string; // superadmin_id
  email: string;
  nombre_completo: string;
  exp: number;
  iat: number;
}

export interface AuditoriaAutenticacion {
  id: string;
  tenant_id: string | null;
  usuario_id: string | null;
  email_intentado: string;
  tipo: TipoAuditoriaAutenticacion;
  ip: string | null;
  user_agent: string | null;
  creado_en: string;
}

// Envoltorio de respuesta estándar de la API.
export type Sobre<T> = SobreExitoso<T> | SobreError;

export interface SobreExitoso<T> {
  exito: true;
  datos: T;
}

export interface SobreError {
  exito: false;
  error: {
    codigo: string;
    mensaje: string;
    detalles?: unknown;
  };
}

// Payload del JWT de sesión.
export interface PayloadSesion {
  sub: string; // usuario_id
  tenant_id: string;
  rol: Rol;
  email: string;
  nombre_completo: string;
  exp: number;
  iat: number;
}

export interface CredencialesLogin {
  slug: string;
  email: string;
  password: string;
}

export interface UsuarioAutenticado {
  usuario: Usuario;
}
